// image-suite/main.js — 套图页入口
//
// 职责：
//   1) 鉴权：api.me() 401 → 跳独立登录入口
//   2) URL 路由：?fromListing=  / ?fromBatchJob=  / ?projectId=  / ?fromLegacy=1
//   3) 统一直入新版 LinkFox Step7 操作区
//
// 严格遵守 P0 不变量（spec §11 / 审批稿 §11）：
//   - fromListing 永远是 savedListingId，不会传 batch jobId
//   - 页面间数据传递只走 URL query string
//   - 前端永远不传 userId
//   - 图片操作区只从 URL intent 和 Step7 状态启动，不再经过旧 mode adapter

import { fetchCurrentUser, logoutUser, getStoredUser, clearToken } from '../../lib/auth.js';
import { setState, setStateBulk } from '../../state.js';
import { initStep7 } from '../../step7-image.js';
import { createStep7PanelFragment } from '../../step7/step7-template.js';
import { autoBindCurrentListing } from '../../step7/listing-bind.js';
import { showToast } from '../../toast.js';
import { renderAppVersionBadge } from '../../app-version-badge.js';
import { getLoginPath, getLogoutPath } from '../../auth-landing.js';
import { parseProjectFromUrl } from '../shared/project-bind.js';

const $ = (sel) => document.querySelector(sel);

// ── DOM 引用 ────────────────────────────────────────────────────
const els = {
  topbarUser: () => $('#user-name'),
  userDropdown: () => $('#user-dropdown'),
  logoutBtn: () => $('#logout-btn'),
  banner: () => $('#suite-legacy-banner'),
  errorBox: () => $('#suite-error'),
  workshopStage: () => $('#suite-workshop'),
};

function showWorkshop() {
  els.workshopStage()?.classList.add('active');
}

function showError(msg) {
  const box = els.errorBox();
  if (!box) return;
  box.textContent = msg;
  box.classList.add('active');
}

function clearError() {
  const box = els.errorBox();
  if (!box) return;
  box.textContent = '';
  box.classList.remove('active');
}

function showLegacyBanner() {
  const el = els.banner();
  if (el) el.classList.add('active');
}

// ── URL 解析 ────────────────────────────────────────────────────
function parseUrlIntent() {
  const params = new URLSearchParams(window.location.search);
  const fromLegacy = params.get('fromLegacy') === '1';

  // 优先级：projectId > fromListing > fromBatchJob > 无
  // fromListing 永远是 savedListingId，永远不传 batch jobId。
  const projectId = params.get('projectId');
  const fromListing = params.get('fromListing');
  const fromBatchJob = params.get('fromBatchJob');

  if (projectId) return { kind: 'legacy-project', projectId, fromLegacy };
  if (fromListing) return { kind: 'saved-listing', savedListingId: fromListing, fromLegacy };
  if (fromBatchJob) return { kind: 'batch-draft', batchJobId: fromBatchJob, fromLegacy };
  return { kind: 'select-mode', fromLegacy };
}

// ── 顶栏鉴权与用户下拉 ──────────────────────────────────────────
function bindUserDropdown() {
  const dropdown = els.userDropdown();
  const trigger = els.topbarUser();
  const logoutBtn = els.logoutBtn();
  if (!dropdown || !trigger) return;

  trigger.addEventListener('click', (ev) => {
    ev.stopPropagation();
    dropdown.classList.toggle('open');
  });
  document.addEventListener('click', (ev) => {
    if (!dropdown.contains(ev.target)) dropdown.classList.remove('open');
  });
  if (logoutBtn) {
    logoutBtn.addEventListener('click', async () => {
      await logoutUser();
      window.location.href = getLogoutPath();
    });
  }
}

function renderUserName(user) {
  const el = els.topbarUser();
  if (!el) return;
  const name = user?.username || user?.email || user?.name || '已登录用户';
  el.textContent = name;
}

async function ensureAuthOrRedirect() {
  // HTML 公开 200，前端 me() 401 后跳独立登录入口。
  const cached = getStoredUser();
  if (cached) renderUserName(cached);
  try {
    const me = await fetchCurrentUser();
    renderUserName(me?.user || me);
    return true;
  } catch (err) {
    if (err && err.status === 401) {
      clearToken();
      window.location.href = getLoginPath();
      return false;
    }
    // 其它错误不阻断，但展示错误提示
    showError('无法获取用户信息，请稍后重试');
    return true;
  }
}

// ── 新版 LinkFox 操作区 ──────────────────────────────────────────
async function launchLinkFoxWorkshop(intent = {}) {
  clearError();
  showWorkshop();
  const workshopHost = els.workshopStage();
  // step7-template.js 是唯一 DOM 源，所有入口统一进入新版 LinkFox 操作区。
  workshopHost.replaceChildren(createStep7PanelFragment({ context: 'image-suite' }));

  setStateBulk({
    workflowMode: 'assets',
    currentStep: 7,
    highestStep: 7,
    imageOnlyProjectId: intent.projectId || null,
    savedListingId: intent.savedListingId || null,
    batchJobId: intent.batchJobId || null,
    assetsProjectName: intent.projectId ? `LinkFox 项目 #${intent.projectId}` : 'LinkFox 商品套图',
    assetsReferencePrompt: '',
    imageSelectedSlots: [1, 2, 3, 4, 5, 6, 7],
  });

  initStep7();

  if (intent.kind === 'saved-listing' && intent.savedListingId) {
    const savedListingId = String(intent.savedListingId).trim();
    if (savedListingId) {
      const showAutoBindToast = (message, isError = false) => {
        showToast(message, {
          id: 'image-suite-autobind-toast',
          type: isError ? 'error' : 'info',
          duration: 2200,
        });
      };
      await autoBindCurrentListing(savedListingId, {
        showImageToast: showAutoBindToast,
        onListingSelected: (listing) => {
          const resolvedId = listing?.id != null ? listing.id : savedListingId;
          if (!resolvedId) return;
          setState('savedListingId', resolvedId);
          setState('imageOnlyProjectId', null);
          if (listing?.name) {
            setState('assetsProjectName', `LinkFox 商品套图 · ${listing.name}`);
          }
        },
        onSellerPointChanged: () => {
          const statusEl = document.getElementById('seller-point-hint');
          if (statusEl) statusEl.textContent = '已通过 listing 自动生成商品信息';
        },
      });
    }
  }
}

// ── 项目绑定 ────────────────────────────────────────────────────
// 项目绑定通过"绑定 Listing"弹窗内的两步流程完成（先选项目再选 Listing）。
// 这里仅解析 URL 中的 projectId 并设置到全局状态，供弹窗预选。
function initProjectBindBar() {
  const initialProjectId = parseProjectFromUrl();
  if (initialProjectId) {
    setState('newProductProjectId', initialProjectId);
  }
}

// ── 主入口 ──────────────────────────────────────────────────────
async function bootstrap() {
  bindUserDropdown();
  const ok = await ensureAuthOrRedirect();
  if (!ok) return;
  renderAppVersionBadge();

  initProjectBindBar();

  const intent = parseUrlIntent();
  if (intent.fromLegacy) showLegacyBanner();

  await launchLinkFoxWorkshop(intent);
}

bootstrap();
